/**
 * Keeping a plan's WORDS in step with the plan's own entitlement.
 *
 * ⚠ THE DEFECT THIS CLOSES, AND IT WAS LIVE (owner, 2026-09-24: "In tha package we have changed the
 * % but in the each package in the title it is still static 10,15 and 20").
 *
 * `MembershipPlan.description` is free text an administrator types in the Admin Panel, and the
 * seeded copy quotes a figure in it — "Mid membership — 15% off eligible charges at every Lifemaan
 * partner." The discount a bill ACTUALLY receives is a different field on a different document:
 * `MembershipPlanVersion.benefitPercent`, which is the one number `benefitEngine.calculateBenefit`
 * reads. Nothing keeps the two in step.
 *
 * Measured on the live catalogue on 2026-09-24, they had genuinely diverged: Plus and Premium had
 * each been republished as a NEW version carrying `benefitPercent: 10`, while their plan documents
 * were untouched and still promised 15% and 20%. A visitor was being offered a discount the card
 * does not give. The percentage was right the day it was typed, which is exactly why nobody saw it
 * go stale.
 *
 * ⚠ SO NO PERCENTAGE REACHES A SCREEN OUT OF AUTHORED COPY. Every one is replaced by the plan's own
 * `benefitPercent`. The rest of the sentence is preserved byte for byte, so the positioning the
 * owner wrote ("Entry membership", "Top membership") survives and only the figure is derived.
 *
 * ⚠ IT NORMALISES **EVERY** PERCENTAGE IN THE SENTENCE, not just the first. No description today
 * quotes two different rates, and a headline that disagreed with the entitlement is far worse than
 * copy that cannot yet express two rates. If Lifemaan ever needs a description quoting a second,
 * genuinely different percentage, that is a product change and needs its own shape — it cannot be
 * bolted on here, because nothing in the text says which figure is the headline one.
 */

/** `10` -> `"10"`, `12.5` -> `"12.5"`, `10.0` -> `"10"`. No trailing zeros in member-facing copy. */
function formatPercent(percent: number): string {
  return String(Number(percent.toFixed(2)));
}

/**
 * The plan's description with every percentage figure in it replaced by `benefitPercent`.
 *
 * Returns the description untouched when there is nothing to derive from — an absent, non-finite or
 * negative `benefitPercent` — so a server that stops sending the field degrades to the authored
 * wording rather than to `NaN% off eligible charges`.
 *
 * The separator between the number and the `%` is preserved, so `"10 %"` stays spaced.
 */
export function describePlanBenefit(
  description: string | null | undefined,
  benefitPercent: number | null | undefined,
  entityPercents?: EntityPercents | null,
): string {
  if (!description) {
    return '';
  }
  const headline = benefitHeadline(benefitPercent, entityPercents);
  if (!headline) {
    return description;
  }
  const derived = formatPercent(headline.percent);
  // When the kinds of partner give different percentages the sentence says "up to", never one figure that is wrong for two of the three.
  const lead = headline.isRange ? 'up to ' : '';
  return description.replace(/(?:up to\s+)?(\d+(?:\.\d+)?)(\s*%)/gi, (_match, _figure: string, tail: string) => `${lead}${derived}${tail}`);
}

/** The discount at each kind of partner, as the server sends it. */
export interface EntityPercents {
  Hospital?: number | null;
  Laboratory?: number | null;
  Radio?: number | null;
}

/**
 * THE ONE FIGURE A HEADLINE MAY QUOTE (owner, 2026-10-16: "in the packages we show 10% off eligible charges, but the percentage is now
 * different for lab, radiology and hospital"). Lifemaan sets the discount per kind of partner, so a single "10%" is true for a hospital
 * and wrong for a laboratory. When the kinds differ, the headline is the HIGHEST of them, worded "up to"; when they agree it is that
 * one figure, exactly as before. The per-kind list always sits beside it for the exact number.
 *
 * @returns null when there is nothing trustworthy to quote
 */
export function benefitHeadline(
  benefitPercent: number | null | undefined,
  entityPercents?: EntityPercents | null,
): { percent: number; isRange: boolean } | null {
  // Radiology is not offered (owner, 2026-10-30), so a stored value never lifts the headline.
  const kinds = [entityPercents?.Hospital, entityPercents?.Laboratory].filter(
    (value): value is number => typeof value === 'number' && Number.isFinite(value) && value > 0,
  );
  if (kinds.length) {
    const distinct = new Set(kinds.map((value) => Number(value.toFixed(2))));
    return { percent: Math.max(...kinds), isRange: distinct.size > 1 };
  }
  if (typeof benefitPercent === 'number' && Number.isFinite(benefitPercent) && benefitPercent >= 0) {
    return { percent: benefitPercent, isRange: false };
  }
  return null;
}

/** `"Up to 20%"` or `"10%"` - the text a card puts in front of "off eligible charges". */
export function benefitHeadlineText(
  benefitPercent: number | null | undefined,
  entityPercents?: EntityPercents | null,
): string {
  const headline = benefitHeadline(benefitPercent, entityPercents);
  if (!headline) {
    return '';
  }
  return `${headline.isRange ? 'Up to ' : ''}${formatPercent(headline.percent)}%`;
}
